import { Edge, HandleType } from 'reactflow';
import { NodeConditionGroup, config } from '../../components/Flow/Nodes/index';
import { AnchorType, FullEdge } from '@components/Flow';
import { Command } from '@store/slices';

export const findAnchor = (id: string | undefined, commands: Command[]) => {
  if (!id) return null;

  const dynamicNodes = ['logic.switch'];
  const dynamicAnchors = commands
    .filter((command) => dynamicNodes.includes(command.config.command_id))
    .map((command) => command.state.condition_groups)
    .flat(5)
    .map((switchGroup) => {
      return {
        id: switchGroup.id,
        label: switchGroup.name,
        type: 'source' as HandleType,
        variant: 'conditional_execution',
      };
    });

  const dataSwitchAnchor: AnchorType = {
    label: 'Data',
    id: 'switch.output_data',
    type: 'source' as HandleType,
    variant: 'data',
  };

  // const anchors = !commands.length
  //   ? Object.keys(config)
  //       .map((key) => config[key].config.anchors)
  //       .flat(5)
  //   : commands.map((command) => command.config.anchors).flat(5);

  const anchors = Object.keys(config)
    .map((key) => config[key].config.anchors)
    .flat(5);

  const dynamicAnchorsWithData = dynamicAnchors.length > 0 ? [dataSwitchAnchor, ...dynamicAnchors] : [];
  const anchor = anchors.concat(dynamicAnchorsWithData).find((x) => x.id === id);

  return anchor;
};

export const getDatabaseAnchor = (id: string | null, connection: any, commands: Command[]) => {
  if (!id) return null;

  const dynamicNodes = ['logic.switch'];
  const dynamicAnchors = commands
    .filter((command) => dynamicNodes.includes(command.config.command_id))
    .map((command) => command.state.condition_groups)
    .flat(5)
    .map((switchGroup) => {
      return {
        id: switchGroup.id,
        label: switchGroup.name,
        type: 'source' as HandleType,
        variant: 'conditional_execution',
      };
    });

  const dataSwitchAnchor: AnchorType = {
    label: 'Data',
    id: 'switch.output_data',
    type: 'source' as HandleType,
    variant: 'data',
  };

  const anchors = Object.keys(config)
    .map((key) => config[key].config.anchors)
    .flat(5);

  const dynamicAnchorsWithData = dynamicAnchors.length > 0 ? [dataSwitchAnchor, ...dynamicAnchors] : [];
  const anchor = anchors.concat(dynamicAnchorsWithData).find((x) => x.id === id);

  return {
    ...anchor,
    source: connection.source,
    sourceHandle: connection.sourceHandle,
    target: connection.target,
    targetHandle: connection.targetHandle,
    variant: connection.variant,
  };
};

export const getCommandsWithDynamicAnchors = (commands: Command[]): Command[] => {
  return commands.map((command) => {
    if (command.config.command_id === 'logic.switch') {
      const anchors = command?.state?.condition_groups?.map((conditionGroup: NodeConditionGroup) => {
        return {
          id: conditionGroup.id,
          label: conditionGroup.name,
          type: 'source' as HandleType,
          variant: 'conditional_execution',
        };
      });

      const connections = command.connections.map((connection) => {
        if (connection.source_anchor === undefined) {
          const newAnchor = findAnchor(connection.edge.sourceHandle as string, commands);
          return { ...connection, source_anchor: newAnchor as AnchorType };
        }

        if (connection.target_anchor === undefined) {
          const newAnchor = findAnchor(connection.edge.targetHandle as string, commands);
          return { ...connection, target_anchor: newAnchor as AnchorType };
        }

        return connection;
      });

      return {
        ...command,
        config: { ...command.config, anchors: command.config.anchors.concat(anchors) },
        connections,
      };
    }

    const connections = command.connections.map((connection) => {
      if (connection.source_anchor === undefined) {
        const newAnchor = findAnchor(connection.edge.sourceHandle as string, commands);
        return { ...connection, source_anchor: newAnchor as AnchorType };
      }

      if (connection.target_anchor === undefined) {
        const newAnchor = findAnchor(connection.edge.targetHandle as string, commands);
        return { ...connection, target_anchor: newAnchor as AnchorType };
      }

      return connection;
    });

    return { ...command, config: { ...command.config }, connections, module };
  });
};

export const getFullEdges = (edges: Edge[], commands: Command[]): FullEdge[] => {
  const full_edges: FullEdge[] = edges.map((edge) => {
    const source_anchor = findAnchor(edge.sourceHandle as string, commands);
    const target_anchor = findAnchor(edge.targetHandle as string, commands);

    return {
      edge: edge as Edge,
      source_anchor: source_anchor as AnchorType,
      target_anchor: target_anchor as AnchorType,
    };
  });

  return full_edges;
};

export const getFullEdge = (id: string | null, edges: Edge[], commands: Command[]): FullEdge | null => {
  if (!id) return null;

  const full_edges = getFullEdges(edges, commands);
  const edge = full_edges.find((x) => x.edge.id === id);

  if (!edge) return null;
  return edge;
};
